import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { OptsImageSingle } from '../optsImageSingle';
import { GUtil } from '../../pdf/gutil';
import { IItemCount, QSingle } from '@educraft/base';
import { fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';

/**
 * PocitaniSlova (Word Counting) - Solve equations to spell words
 *
 * Layout: 3 landscape pages, 4 puzzles per page (2×2).
 * Each puzzle: equations whose results map to letter positions in a word.
 * Students solve equations and place letters to reveal the word.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pocitani_slova
 * Updated to LayoutImageSingle to use arbitrary equation generators.
 */
export class PocitaniSlova implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const wordHeight = PdfUnit.FromMillimeters(30);

    const totalCount = Math.min(opts.list.length, this.countMax());
    const puzzlesPerPage = 4;
    const pagesCount = Math.ceil(totalCount / puzzlesPerPage);

    for (let page = 0; page < pagesCount; page++) {
      const pg = pdf.addPage(true); // landscape
      pg.cutCross(new PdfPoint(PdfSize.A4.height / 2, PdfSize.A4.width / 2));

      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 2; x++) {
          const index = page * puzzlesPerPage + y * 2 + x;
          if (index >= totalCount) continue;

          const item = opts.list[index];
          const word = item.label;
          const {length} = word;

          // Generate equations for each letter using opts.generator
          const equations: { value: string; n: number; letter: string; str: string }[] = [];
          const usedAnswers = new Set<string>();
          for (let i = 0; i < length; i++) {
            let q: QSingle;
            let tries = 0;
            do {
              q = opts.generator.generate(opts.data);
              tries++;
            } while (usedAnswers.has(q.answer) && tries < 100);
            usedAnswers.add(q.answer);
            equations.push({
              value: q.answer,
              n: i,
              letter: word[i],
              str: fixGenArray(q.texts).join(' '),
            });
          }

          const rect = new PdfRect(
            margin + (PdfSize.A4.height / 2) * x,
            margin + (PdfSize.A4.width / 2) * y,
            PdfSize.A4.height / 2 - 2 * margin,
            PdfSize.A4.width / 2 - 2 * margin
          );

          const questionRect = new PdfRect(
            margin + (PdfSize.A4.height / 2) * x,
            margin + (PdfSize.A4.width / 2) * y,
            PdfSize.A4.height / 2 - 2 * margin,
            PdfSize.A4.width / 2 - 2 * margin - wordHeight
          );

          const answerRect = new PdfRect(
            margin + (PdfSize.A4.height / 2) * x,
            PdfSize.A4.width / 2 - wordHeight - margin + (PdfSize.A4.width / 2) * y,
            wordHeight,
            wordHeight
          );

          const wordRect = new PdfRect(
            answerRect.x + answerRect.height,
            answerRect.y,
            rect.width - answerRect.height,
            answerRect.height
          );

          const letterSize = { width: wordRect.width / length, height: wordRect.height / 2 };

          // Draw image
          if (item.image) {
            pg.drawImageCenter(item.image, answerRect);
          }

          // Draw number/letter grid
          for (let i = 0; i < length; i++) {
            const r1 = new PdfRect(
              wordRect.x + letterSize.width * i,
              wordRect.y,
              letterSize.width,
              letterSize.height
            );
            const r2 = new PdfRect(
              wordRect.x + letterSize.width * i,
              wordRect.y + letterSize.height,
              letterSize.width,
              letterSize.height
            );

            pg.drawRectangle(GUtil.basePen, null, r1);
            pg.drawRectangle(GUtil.basePen, null, r2);

            pg.drawFillStringCenter(`${equations[i].value}`, GUtil.baseFont, PdfBrush.Black, r1);
          }

          // Draw randomized equations
          const randomized = [...equations];
          for (let i = randomized.length - 1; i > 0; i--) {
            const j = opts.data.random.next(0, i + 1);
            [randomized[i], randomized[j]] = [randomized[j], randomized[i]];
          }

          let countX = 1;
          let countY = length;

          if (length >= 5) {
            countX = 2;
            countY = Math.ceil(length / countX);
          }
          if (length >= 15) {
            countX = 3;
            countY = Math.ceil(length / countX);
          }

          const qSize = {
            width: questionRect.width / countX,
            height: questionRect.height / countY,
          };

          for (let y1 = 0; y1 < countY; y1++) {
            for (let x1 = 0; x1 < countX; x1++) {
              const i = y1 * countX + x1;
              if (i < length) {
                const r = new PdfRect(
                  questionRect.x + qSize.width * x1,
                  questionRect.y + qSize.height * y1,
                  qSize.width,
                  qSize.height
                );
                const text = `${randomized[i].letter.toUpperCase()}) ${randomized[i].str}`;
                pg.drawString(
                  text,
                  GUtil.baseFont,
                  PdfBrush.Black,
                  null,
                  new PdfRect(r.x + margin / 2, r.y, r.width, r.height),
                  ETextAlign.CenterLeft
                );
              }
            }
          }

          pg.drawRectangle(GUtil.basePen, null, questionRect);
          pg.drawRectangle(GUtil.basePen, null, answerRect);
          pg.drawRectangle(GUtil.basePen, null, rect);
        }
      }
    }
  }
}
